import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaseListComponent } from '@common/classes/base-list';
import { ActionBarGroup } from '@common/components/action-bar/action-bar.interface';
import { FormProperty } from '@common/components/app-control/app-control.component';
import { ValueOf } from '@common/models/util.interface';
import { ViewMode } from '@common/models/view-mode';
import { DialogSettings } from '@progress/kendo-angular-dialog';
import _, { cloneDeep, mergeWith } from 'lodash';
import { setNestedValue, unflattenObject } from './path-utils';

/**
 * Base interface that all items managed by ItemsComponent must implement
 */
export interface BaseItem {
    id?: number;
    active?: boolean;
    sequence?: number;
}

/**
 * Abstract base component for managing collections of sub-entities in a grid with CRUD operations.
 *
 * This component provides:
 * - Grid display with action buttons (Add, Edit, Remove)
 * - Dialog-based form editing
 * - Local state management in create/edit modes
 * - Direct backend persistence in view mode
 * - Permission-based access control
 *
 * @template TModel The type of the entity being managed
 */
@Component({ template: '' })
export abstract class ItemsComponent<TModel extends BaseItem> extends BaseListComponent implements OnInit {
    override queryName: string;
    override selection: TModel[] = [];

    /** Backend command name for saving items (e.g., 'SaveContractVessel') */
    protected abstract command: string;

    /** Display name for the item type (e.g., 'Contract Vessel') */
    protected abstract itemName: string;

    /** Permission key required to create items */
    protected abstract itemCreatePermission: string;

    /** Permission key required to edit items */
    protected abstract itemEditPermission: string;

    /** Permission key required to remove items */
    protected abstract itemRemovePermission: string;

    /** Dialog configuration for create/edit forms */
    protected dialogSettings: DialogSettings = { width: '50vw' };

    /** Form field definitions for the create/edit dialog */
    protected abstract dialogProperties: FormProperty[];

    /** Grid action buttons configuration */
    protected gridButtons: ActionBarGroup[];

    protected itemIdentifier: keyof TModel = 'id';

    /** Sets the data array and refreshes the display */
    @Input() set data(val: Partial<TModel>[]) {
        this._modelData = val ?? [];

        const currentIds = new Set(this._modelData.map((item) => item[this.itemIdentifier]));
        this.selectedIds = new Set([...this.selectedIds].filter((id) => currentIds.has(id as any)));

        this.refreshDisplay();
    }
    @Input() initialModel: TModel = {} as TModel;
    @Input() mode: ValueOf<typeof ViewMode>;
    /** Name of the reference to the parent entity in camel case, for example: vesselVisitId */
    @Input() canConfirm?: (model: any) => boolean = () => true;
    @Input() parentEntityReference: string;
    @Input() parentEntityId: number;
    @Input() actionsVisible = true;
    @Input() addVisible = true;
    @Input() editVisible = true;
    @Input() removeVisible = true;
    @Input() actionsDisabled: boolean | (() => boolean) | undefined = undefined;
    @Input() addDisabled: boolean | undefined = undefined;
    @Input() editDisabled: boolean | undefined = undefined;
    @Input() removeDisabled: boolean | undefined = undefined;

    /** @deprecated Use itemChanged instead */
    @Output() protected created = new EventEmitter();
    @Output() protected mutatedLocally = new EventEmitter();

    /** Emitted when an item is created/edited/removed */
    @Output() protected itemChanged = new EventEmitter<{ action: string; item: Partial<TModel> | null }>();

    /** Internal data storage */
    private _modelData: Partial<TModel>[] = [];

    /** Data displayed in the grid (may differ from _modelData in create/edit modes) */
    private _displayData: Partial<TModel>[] = [];

    protected selectedIds = new Set<number | string>();

    override ngOnInit(): void {
        // // If we want to disable actions in view mode, we can do it like this:
        // if (this.actionsDisabled === undefined) {
        //     this.actionsDisabled = this.mode === ViewMode.view;
        // }

        this.gridButtons = [
            {
                label: 'Actions',
                items: [
                    {
                        tooltip: 'Add new',
                        icon: 'faSolidPlus',
                        iconOnly: true,
                        variant: 'primary',
                        isVisible: () =>
                            this.actionsVisible &&
                            this.addVisible &&
                            this.user.hasPermission(this.itemCreatePermission),
                        isDisabled: () => this.isActionsDisabled() || this.addDisabled,
                        onClick: async () => await this.create()
                    },
                    {
                        tooltip: 'Edit',
                        icon: 'faSolidPenToSquare',
                        iconOnly: true,
                        variant: 'primary',
                        isVisible: () =>
                            this.actionsVisible && this.editVisible && this.user.hasPermission(this.itemEditPermission),
                        isDisabled: () => this.editDisabled || this.isActionsDisabled() || this.selection.length !== 1,
                        onClick: async () => await this.openDialog(this.selection[0])
                    },
                    {
                        tooltip: 'Remove',
                        icon: 'faSolidTrash',
                        iconOnly: true,
                        variant: 'danger',
                        isVisible: () =>
                            this.actionsVisible &&
                            this.removeVisible &&
                            this.user.hasPermission(this.itemRemovePermission),
                        isDisabled: () =>
                            this.removeDisabled || this.isActionsDisabled() || this.selection.length !== 1,
                        onClick: async () => {
                            await this.remove(this.selection[0]);
                            this.onSelectedIdsChange([]);
                        }
                    }
                ]
            }
        ];
    }

    /** Gets the current data array */
    public get data(): Partial<TModel>[] {
        return this._modelData;
    }

    /** Gets the data to display in the grid */
    protected get displayData(): Partial<TModel>[] {
        return this._displayData;
    }

    public async openDialog(item: any = null): Promise<void> {
        const action = item ? ViewMode.edit : ViewMode.create;
        let data = await this.dialogService.form({
            options: {
                title: `${item ? this.translateService.instant('Edit') : this.translateService.instant('Add')}: ${this.translateService.instant(this.itemName)}`,
                properties:
                    this.selection.length > 0
                        ? this.dialogService.initializeFormProperties(this.dialogProperties, item)
                        : this.dialogProperties,
                canConfirm: this.canConfirm,
                confirmText: `${item ? 'Save' : 'Create'}`
            },
            dialogSettings: this.dialogSettings
        });
        if (!data) return;

        if (this.initialModel) data = { ...structuredClone(this.initialModel), ...data };
        if (item) {
            const cloned = cloneDeep(item);
            Object.entries(data).forEach(([prop, value]) => {
                if (Array.isArray(value) && value.length === 0) return;
                setNestedValue(cloned, prop, value);
            });
            item = cloned;
        } else {
            const nestedData = unflattenObject({ ...data, active: true });
            item = {
                ...nestedData,
                id: this.newItemId(),
                active: true,
                sequence: _.sortBy(this._modelData, 'sequence')?.at(-1)?.sequence + 1 || 1,
                [this.parentEntityReference]: this.parentEntityId
            };
        }

        await this.executeItemsAction(this.command, item, action);
    }

    public onSelectedIdsChange(ids: (number | string)[]): void {
        this.selectedIds = new Set(ids);
        this.deriveSelection();
    }

    /**
     * Handles row reordering events from the grid
     * Updates the source data with the new sequence numbers
     */
    public onRowReordered(event: { data: any[]; sequenceField: string }): void {
        if (!event.data || !event.sequenceField) return;

        // Create a map of updates to apply (id → new sequence number)
        const updates = new Map<number | string, number>();

        event.data.forEach((reorderedItem) => {
            const key = reorderedItem.id;
            if (key) updates.set(key, reorderedItem[event.sequenceField]);
        });

        // Apply all updates to the original data
        this._modelData.forEach((originalItem) => {
            const key = (originalItem as any).id;
            if (updates.has(key)) {
                (originalItem as any)[event.sequenceField] = updates.get(key);
            }
        });

        this.refreshDisplay();
        this.itemChanged.emit({ action: 'reordered', item: null });
    }

    protected isActionsDisabled(): boolean {
        return typeof this.actionsDisabled === 'function' ? this.actionsDisabled() : this.actionsDisabled;
    }

    protected async create(): Promise<void> {
        await this.openDialog();
    }

    protected async remove(item: any): Promise<void> {
        const res = await this.dialogService.confirm({
            options: {
                title: this.commonService.translateService.instant(
                    `${this.translateService.instant('Remove')}: ${this.translateService.instant(this.itemName)}`
                ),
                message: `${this.commonService.translateService.instant(
                    'Are you sure you want to remove the selected item'
                )}?`
            }
        });
        if (!res) return;

        item.active = false;

        await this.executeItemsAction(this.command, item, this.mode === ViewMode.view ? ViewMode.edit : 'remove');
    }

    /**
     * Executes an action on an item, either locally or on the backend
     * @param command The backend command name
     * @param item The item to act upon
     * @param action The action type: 'create', 'edit', or 'remove'
     */
    protected async executeItemsAction(command: string, item: any, action: string): Promise<any> {
        if (this.mode === ViewMode.create || this.mode === ViewMode.edit) {
            // In create/edit mode: store changes locally
            this.mutateLocally(item, action);
        } else {
            // In view mode: persist to backend immediately
            await this.executeCommand({
                command,
                data: { saveModel: item, action }
            }).then(() => this.emitCreated());
        }
    }

    /**
     * Emits an event when an item is changed
     * @deprecated Use itemChanged output instead
     */
    protected emitCreated(): void {
        this.created.emit();
        this.itemChanged.emit({ action: 'changed', item: null });
    }

    /**
     * Updates the local data array without persisting to backend
     * @param item The item to mutate
     * @param action The mutation type
     */
    private mutateLocally(item: any, action: string): void {
        const itemIndex = this.findItemIndex(item);

        switch (action) {
            case ViewMode.create:
                this.addNewItem(item);
                break;

            case ViewMode.edit:
                if (itemIndex > -1) {
                    this.updateExistingItem(itemIndex, item);
                }
                break;

            case 'remove':
                if (itemIndex > -1) {
                    this.removeItem(itemIndex, item);
                }
                break;
        }

        this.mutatedLocally.emit();
        this.refreshDisplay();
    }

    /**
     * Finds the index of an item in the data array
     */
    private findItemIndex(item: any): number {
        return this._modelData.findIndex((dataItem) => dataItem.id === item.id);
    }

    /**
     * Adds a new item to the data array
     */
    private addNewItem(item: any): void {
        this._modelData.push(item);
    }

    /**
     * Updates an existing item in the data array
     */
    private updateExistingItem(index: number, updatedItem: any): void {
        const existingItem = { ...this._modelData[index] };

        // Custom merge that preserves arrays in the target object
        const mergedItem = mergeWith(existingItem, updatedItem, (targetValue, sourceValue) => {
            if (Array.isArray(sourceValue)) {
                return targetValue; // Keep existing arrays
            }
            return undefined; // Use default merge behavior
        });

        // Replace with a new object reference to trigger change detection
        this._modelData[index] = { ...mergedItem };
    }

    /**
     * Removes an item from the data array
     */
    private removeItem(index: number, item: any): void {
        if (item.id && item.id > 0) {
            // Existing items: soft delete
            this._modelData[index].active = false;
        } else {
            // New items: remove completely
            this._modelData.splice(index, 1);
        }

        this.selectedIds.delete(item[this.itemIdentifier]);
        this.deriveSelection();
    }

    /**
     * Refreshes the display data based on the current mode
     */
    private refreshDisplay(): void {
        // In create/edit modes, create a new array reference for change detection
        if (this.mode === ViewMode.create || this.mode === ViewMode.edit) {
            // Filter out soft-deleted items (active = false) so they disappear from grid immediately
            this._displayData = [...this._modelData.filter((item) => item.active !== false)];
        } else {
            this._displayData = this._modelData;
        }

        this.deriveSelection();
    }

    private deriveSelection(): void {
        if (!this._modelData || this._modelData.length === 0) {
            this.selection = [];
            return;
        }

        this.selection = this._modelData.filter((item) =>
            this.selectedIds.has(item[this.itemIdentifier] as number | string)
        ) as TModel[];
    }

    private newItemId(): number {
        return (
            this._modelData
                .filter((item) => item.id! <= 0)
                .sort((a, b) => a.id - b.id)
                .at(0)?.id - 1 || 0
        );
    }
}
